import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/components/admin/contact-details/types";
import { useToast } from "@/hooks/use-toast";

export const useProfileData = (id?: string) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchProfile = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Validate UUID format
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
        throw new Error("Invalid contact ID format");
      }

      console.log("Fetching profile for ID:", id);
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select()
        .eq('id', id)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        throw new Error(profileError.message);
      }

      if (!data) {
        console.log("No profile found for ID:", id);
        setProfile(null);
        return;
      }

      console.log("Profile data retrieved:", data);
      setProfile(data);
    } catch (err) {
      const error = err as Error;
      console.error("Error in fetchProfile:", error);
      setError(error);
      toast({
        variant: "destructive",
        title: "Error Loading Contact",
        description: error.message || "Failed to load contact details"
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  const updateProfile = useCallback(async (updatedProfile: Partial<Profile>) => {
    if (!id) return;

    try {
      console.log("Updating profile:", { id, updatedProfile });
      
      const { data, error: updateError } = await supabase
        .from('profiles')
        .update(updatedProfile)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating profile:', updateError);
        throw updateError;
      }

      if (!data) {
        throw new Error('Profile update failed - no data returned');
      }

      console.log("Profile updated successfully:", data);
      setProfile(data);
      
      return data;
    } catch (err) {
      console.error('Error in updateProfile:', err);
      throw err;
    }
  }, [id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { 
    profile, 
    loading, 
    error, 
    refetch: fetchProfile, 
    updateProfile 
  };
};